import React from 'react';
import { Card, CardContent, Typography, Box, Chip, Divider } from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat, Flight, Place } from '@mui/icons-material';

interface AirportPassengerData {
  monthly: {
    current: number;
    previous_year: number;
    change_percent: number | null;
    period_month?: number;
    period_year?: number;
  };
  ytd: {
    current: number;
    previous_year: number;
    change_percent: number | null;
    vs_precovid: number | null;
    precovid_value: number;
    period_year?: number;
  };
}

interface EnhancedAirportCardProps {
  airportCode: string;
  airportName: string;
  city?: string;
  state?: string;
  distance_km?: number;
  passengers?: AirportPassengerData;
  viewMode?: 'current' | 'ytd' | 'both';
  onClick?: () => void;
}

export const EnhancedAirportCard: React.FC<EnhancedAirportCardProps> = ({
  airportCode,
  airportName,
  city,
  state,
  passengers,
  viewMode = 'both',
  onClick,
}) => {
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const getTrendIcon = (change: number | null | undefined) => {
    if (change === null || change === undefined) return <TrendingFlat sx={{ fontSize: 16 }} />;
    if (change > 1) return <TrendingUp color="success" sx={{ fontSize: 16 }} />;
    if (change < -1) return <TrendingDown color="error" sx={{ fontSize: 16 }} />;
    return <TrendingFlat sx={{ fontSize: 16 }} />;
  };

  const getChangeColor = (change: number | null | undefined) => {
    if (change === null || change === undefined) return 'text.secondary';
    return change >= 0 ? 'success.main' : 'error.main';
  };

  const formatNumber = (val: number) => {
    if (val >= 1000000) {
      return `${(val / 1000000).toFixed(1)}M`;
    }
    if (val >= 1000) {
      return `${(val / 1000).toFixed(0)}K`;
    }
    return val.toLocaleString('en-US');
  };

  const formatFullNumber = (val: number) => {
    return val.toLocaleString('en-US');
  };

  if (!passengers) {
    return null;
  }

  const showMonthly = viewMode === 'current' || viewMode === 'both';
  const showYTD = viewMode === 'ytd' || viewMode === 'both';

  return (
    <Card
      onClick={onClick}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        height: '100%',
        '&:hover': onClick
          ? {
              transform: 'scale(1.02)',
              boxShadow: 3,
            }
          : {},
      }}
    >
      <CardContent>
        {/* Header with period chip in upper right */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Flight sx={{ fontSize: 20, color: 'primary.main' }} />
              <Typography variant="h6" fontWeight="bold">
                {airportCode}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" noWrap>
              {airportName}
            </Typography>
            {(city || state) && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <Place sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {city}{city && state ? ', ' : ''}{state}
                </Typography>
              </Box>
            )}
          </Box>
          {showMonthly && passengers.monthly.period_month && passengers.monthly.period_year && (
            <Chip
              label={`${monthNames[passengers.monthly.period_month - 1]} ${passengers.monthly.period_year}`}
              size="small"
              variant="outlined"
              sx={{ borderRadius: '4px' }}
            />
          )}
        </Box>

        {/* Monthly Passengers Section */}
        {showMonthly && (
          <Box mb={showYTD ? 2.5 : 0}>

            <Box display="flex" alignItems="baseline" gap={1} mb={1}>
              <Typography variant="h4" fontWeight="bold">
                {formatNumber(passengers.monthly.current)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                passengers
              </Typography>
            </Box>

            {/* Last Year and YoY Change on same line */}
            <Box display="flex" alignItems="center" justifyContent="space-between">
              {passengers.monthly.previous_year > 0 && (
                <Typography variant="body2" color="text.secondary">
                  Last Year: {formatFullNumber(passengers.monthly.previous_year)}
                </Typography>
              )}

              {passengers.monthly.change_percent !== null && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  {getTrendIcon(passengers.monthly.change_percent)}
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    color={getChangeColor(passengers.monthly.change_percent)}
                  >
                    {passengers.monthly.change_percent > 0 ? '+' : ''}
                    {passengers.monthly.change_percent.toFixed(1)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    YoY
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Divider between sections */}
        {showMonthly && showYTD && (
          <Divider sx={{ my: 2 }} />
        )}

        {/* YTD Passengers Section */}
        {showYTD && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              YTD {passengers.ytd.period_year || 'Latest'}
            </Typography>

            <Box display="flex" alignItems="baseline" gap={1} mb={1}>
              <Typography variant="h4" fontWeight="bold">
                {formatNumber(passengers.ytd.current)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                passengers
              </Typography>
            </Box>

            {passengers.ytd.previous_year > 0 && (
              <Typography variant="body2" color="text.secondary">
                Last Year: {formatFullNumber(passengers.ytd.previous_year)}
              </Typography>
            )}

            {passengers.ytd.change_percent !== null && (
              <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                {getTrendIcon(passengers.ytd.change_percent)}
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  color={getChangeColor(passengers.ytd.change_percent)}
                >
                  {passengers.ytd.change_percent > 0 ? '+' : ''}
                  {passengers.ytd.change_percent.toFixed(1)}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  YoY
                </Typography>
              </Box>
            )}

            {/* COVID Recovery */}
            {passengers.ytd.vs_precovid !== null && (
              <Box
                mt={1.5}
                pt={1.5}
                borderTop={1}
                borderColor="divider"
              >
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  COVID Recovery
                </Typography>
                <Box display="flex" alignItems="center" gap={0.5}>
                  {getTrendIcon(passengers.ytd.vs_precovid)}
                  <Typography variant="body2" color={getChangeColor(passengers.ytd.vs_precovid)}>
                    {passengers.ytd.vs_precovid > 0 ? '+' : ''}{passengers.ytd.vs_precovid.toFixed(1)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    vs 2019 YTD
                  </Typography>
                </Box>
                {passengers.ytd.precovid_value > 0 && (
                  <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                    2019 YTD: {formatFullNumber(passengers.ytd.precovid_value)}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};